/**
 * Country normalization — turn whatever a guest CSV holds in its country column
 * ("Australia", "AU", "United Kingdom", "UK"…) into an ISO-3166 alpha-2 code and
 * a clean display name. Names come from the browser's own locale data (Intl), so
 * we only embed the code list, not 250 hand-typed names.
 */

// Every ISO-3166-1 alpha-2 code (so we can accept a raw code directly).
const ISO_CODES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(
    ' ',
  );
const CODE_SET = new Set(ISO_CODES);

let regionNames: Intl.DisplayNames | null = null;
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch {
  regionNames = null;
}

export function codeToName(code: string): string {
  return (regionNames?.of(code) ?? code) || code;
}

// name (lowercased) → code, built from the browser's country names + a few aliases.
const NAME_TO_CODE = new Map<string, string>();
for (const code of ISO_CODES) {
  const name = regionNames?.of(code);
  if (name) NAME_TO_CODE.set(name.toLowerCase(), code);
}
const ALIASES: Record<string, string> = {
  uk: 'GB',
  'u.k.': 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  usa: 'US',
  'u.s.a.': 'US',
  'u.s.': 'US',
  america: 'US',
  'united states of america': 'US',
  uae: 'AE',
  'u.a.e.': 'AE',
  'south korea': 'KR',
  'north korea': 'KP',
  russia: 'RU',
  vietnam: 'VN',
  'ivory coast': 'CI',
  'czech republic': 'CZ',
  czechia: 'CZ',
  holland: 'NL',
  'the netherlands': 'NL',
  burma: 'MM',
};
for (const [alias, code] of Object.entries(ALIASES)) NAME_TO_CODE.set(alias, code);

/** Resolve one raw country cell to { code, name }, or null if unrecognizable. */
export function normalizeCountry(raw: string): { code: string; name: string } | null {
  const v = (raw ?? '').trim();
  if (!v) return null;
  const upper = v.toUpperCase();
  if (upper.length === 2 && CODE_SET.has(upper)) return { code: upper, name: codeToName(upper) };
  const code = NAME_TO_CODE.get(v.toLowerCase());
  return code ? { code, name: codeToName(code) } : null;
}
